import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  CalendarCheck,
  CheckCircle2,
  Clock,
  DatabaseZap,
  FileSignature,
  LineChart,
  Megaphone,
  MessageCircle,
  PlugZap,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { WHATSAPP_LINK } from '../constants';

type UseCaseKey = 'clinicas' | 'estetica' | 'ventas' | 'integraciones';

type UseCase = {
  key: UseCaseKey;
  path: string;
  eyebrow: string;
  title: string;
  highlight: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  cta: string;
  whatsappText: string;
  heroImage: string;
  heroImageAlt: string;
  who: string[];
  outcomes: string[];
  modules: Array<{ title: string; description: string; icon: LucideIcon }>;
  integrations: string[];
  proof: string;
  faq: Array<{ q: string; a: string }>;
};

export const USE_CASES: Record<UseCaseKey, UseCase> = {
  clinicas: {
    key: 'clinicas',
    path: '/clinicas-centros-agenda',
    eyebrow: 'Clínicas y centros con agenda',
    title: 'IA para responder pacientes, gestionar agenda y rellenar huecos libres',
    highlight: 'Sin cambiar tu software actual.',
    description:
      'Ancora conecta WhatsApp, voz y automatizaciones con tu agenda o CRM para atender mejor, reducir trabajo manual y no perder reservas por falta de respuesta.',
    seoTitle: 'IA para clínicas y centros con agenda | Ancora Automations',
    seoDescription:
      'IA conectada a WhatsApp, voz, agenda y CRM para clínicas y centros con agenda: responde pacientes, gestiona reservas y rellena huecos libres.',
    cta: 'Dinos qué agenda usas',
    whatsappText: 'Hola, tengo una clínica o centro con agenda y quiero ver cómo Ancora puede conectarse con mi software actual.',
    heroImage: '/persona-tecnico-setup.jpg',
    heroImageAlt: 'Profesional revisando una automatización para agenda y atención con IA',
    who: ['Fisioterapia', 'Psicología', 'Nutrición', 'Clínicas privadas', 'Centros de bienestar', 'Servicios recurrentes'],
    outcomes: [
      'Responder dudas frecuentes por WhatsApp 24/7',
      'Gestionar reservas, cambios y cancelaciones',
      'Enviar recordatorios y confirmaciones automáticas',
      'Usar lista de espera para aprovechar huecos libres',
      'Escalar al equipo humano cuando haga falta criterio profesional',
    ],
    modules: [
      { title: 'Atención WhatsApp y voz', description: 'La IA responde primeras consultas, precios orientativos, disponibilidad y derivaciones al equipo.', icon: MessageCircle },
      { title: 'Agenda conectada', description: 'Conexión con calendario, CRM o software de reservas para evitar doble trabajo.', icon: CalendarCheck },
      { title: 'Recordatorios y recuperación', description: 'Confirmaciones, cambios, reprogramaciones y avisos para llenar huecos.', icon: Clock },
      { title: 'Panel de seguimiento', description: 'Visibilidad de conversaciones, oportunidades y próximas acciones.', icon: LineChart },
    ],
    integrations: ['Google Calendar', 'TIMP', 'Flowww', 'CRM', 'Gmail', 'Google Sheets', 'WhatsApp Business API'],
    proof: 'Ideal si ahora dependes de responder manualmente WhatsApp, llamadas o mensajes de Instagram para cerrar reservas.',
    faq: [
      { q: '¿Sustituye mi software de agenda?', a: 'No. La idea es conectarlo cuando sea posible y mantener vuestra operativa actual.' },
      { q: '¿La IA puede confirmar reservas sola?', a: 'Depende del nivel de integración y de las reglas que definamos. En flujos sensibles puede dejar la reserva lista para aprobación humana.' },
      { q: '¿Sirve para varios profesionales?', a: 'Sí, se puede adaptar a agendas por profesional, servicio, duración y disponibilidad.' },
    ],
  },
  estetica: {
    key: 'estetica',
    path: '/centros-estetica-belleza',
    eyebrow: 'Estética, belleza y bienestar',
    title: 'Automatiza WhatsApp, reservas, reseñas y campañas sin cambiar Flowww, Booksy o Treatwell',
    highlight: 'Más reservas y menos seguimiento manual.',
    description:
      'Conectamos la IA a tus canales y herramientas para atender clientas, recuperar oportunidades, publicar mejor y activar campañas segmentadas cuando tenga sentido.',
    seoTitle: 'IA para estética, belleza y bienestar | Ancora Automations',
    seoDescription:
      'Automatiza WhatsApp, reservas, reseñas, Instagram y Google Business en centros de estética y belleza sin cambiar Flowww, Booksy o Treatwell.',
    cta: 'Cuéntanos qué software usas',
    whatsappText: 'Hola, tengo un centro de estética/belleza y quiero ver cómo Ancora puede conectar WhatsApp, reservas y marketing con mi software actual.',
    heroImage: '/persona-mujer-oficina.jpg',
    heroImageAlt: 'Profesional gestionando reservas, reseñas y marketing desde una oficina',
    who: ['Centros de estética', 'Clínicas estéticas', 'Peluquerías', 'Barberías', 'Uñas y cejas', 'Masajes y bienestar'],
    outcomes: [
      'Responder por WhatsApp aunque el equipo esté atendiendo',
      'Conectar reservas con Flowww, Booksy, Treatwell o calendario',
      'Pedir reseñas tras la visita y responderlas con tono de marca',
      'Reactivar clientas inactivas con campañas aprobadas',
      'Publicar en Instagram y Google Business con enlaces medibles',
    ],
    modules: [
      { title: 'Reservas por WhatsApp', description: 'Primer filtro, disponibilidad, cambios y recordatorios conectados a la operativa.', icon: CalendarCheck },
      { title: 'Marketing IA', description: 'Ideas, posts, campañas y reactivación basadas en servicios, temporada y oportunidades reales.', icon: Megaphone },
      { title: 'Reseñas y reputación', description: 'Solicitud post-visita y respuestas coherentes en Google Business Profile.', icon: Star },
      { title: 'Integraciones de belleza', description: 'Trabajo alrededor de Flowww, Booksy, Treatwell, TIMP, Gmail, Sheets o APIs.', icon: PlugZap },
    ],
    integrations: ['Flowww', 'Booksy', 'Treatwell', 'TIMP', 'Instagram', 'Google Business Profile', 'WhatsApp', 'Google Calendar'],
    proof: 'Pensada para centros donde cada mensaje no respondido puede acabar siendo una reserva perdida.',
    faq: [
      { q: '¿Las campañas están incluidas en el precio base?', a: 'No. El WhatsApp Base empieza desde 9,99€/mes; campañas, Instagram, integraciones y marketing IA se presupuestan como módulos.' },
      { q: '¿Puede publicar automáticamente en Instagram?', a: 'Puede ser automático o asistido según el nivel de aprobación que quieras mantener.' },
      { q: '¿Se puede usar con Treatwell o Booksy?', a: 'Sí, la propuesta es conectar o rodear esas herramientas sin obligarte a cambiar de sistema.' },
    ],
  },
  ventas: {
    key: 'ventas',
    path: '/agentes-ventas-ia',
    eyebrow: 'Agentes de ventas IA',
    title: 'Convierte mensajes de WhatsApp, Instagram, email y formularios en oportunidades con seguimiento',
    highlight: 'Ningún lead olvidado.',
    description:
      'Ancora captura interesados desde tus canales existentes, resume la oportunidad, propone la siguiente acción y mantiene un pipeline visible para vender con más control.',
    seoTitle: 'Agentes de ventas IA para WhatsApp e Instagram | Ancora Automations',
    seoDescription:
      'Agentes de ventas IA para capturar, cualificar y seguir leads desde WhatsApp, Instagram, email y formularios con pipeline visible y aprobación humana.',
    cta: 'Quiero ver mi pipeline IA',
    whatsappText: 'Hola, quiero ver cómo funcionaría un agente de ventas IA para capturar, cualificar y hacer seguimiento de mis leads.',
    heroImage: '/persona-grupo-reunion.jpg',
    heroImageAlt: 'Equipo revisando oportunidades comerciales y seguimiento de leads',
    who: ['Negocios con leads por WhatsApp', 'Servicios de alto valor', 'Centros con presupuestos', 'Academias', 'Clínicas', 'Equipos comerciales pequeños'],
    outcomes: [
      'Detectar interesados desde conversaciones reales',
      'Clasificar servicio, urgencia, presupuesto y objeciones',
      'Crear pipeline con estados y próximas acciones',
      'Preparar respuestas o seguimientos para aprobación',
      'Medir qué canales generan oportunidades reales',
    ],
    modules: [
      { title: 'Captura automática', description: 'Leads desde WhatsApp, Instagram, formularios, email o CRM ligero.', icon: Users },
      { title: 'Cualificación', description: 'Resumen semántico del interés, urgencia, valor potencial y objeciones.', icon: Bot },
      { title: 'Pipeline visible', description: 'Estados, próxima acción y recordatorios para que el seguimiento no dependa de memoria.', icon: LineChart },
      { title: 'Review-only por defecto', description: 'El sistema prepara acciones; los mensajes sensibles salen con aprobación humana.', icon: CheckCircle2 },
    ],
    integrations: ['WhatsApp', 'Instagram', 'Gmail', 'Google Sheets', 'CRM', 'Formularios', 'Google Analytics'],
    proof: 'Especialmente útil cuando llegan consultas pero el seguimiento se pierde entre conversaciones, notas y tareas sueltas.',
    faq: [
      { q: '¿El agente contacta leads automáticamente?', a: 'Por defecto no. Trabajamos en modo asistido/review-only para evitar envíos sensibles sin aprobación.' },
      { q: '¿Necesito tener CRM?', a: 'No necesariamente. Puede empezar con Google Sheets o un panel ligero y luego conectarse a un CRM.' },
      { q: '¿Sirve para leads de Instagram?', a: 'Sí, si el canal está habilitado, puede centralizar oportunidades de Instagram junto a WhatsApp, email o formularios.' },
    ],
  },
  integraciones: {
    key: 'integraciones',
    path: '/integraciones-software-ia',
    eyebrow: 'Integraciones con software existente',
    title: 'No cambies de software. Conectamos la IA con la herramienta que ya usas',
    highlight: 'La IA trabaja encima de tu operativa.',
    description:
      'Ancora se conecta con agendas, CRMs, hojas de cálculo, email, WhatsApp, Instagram y Google Business para automatizar sin forzar migraciones innecesarias.',
    seoTitle: 'Integraciones de IA con tu software actual | Ancora Automations',
    seoDescription:
      'Conecta IA con agenda, CRM, WhatsApp, Instagram, Google Business, Gmail, Sheets y software de reservas sin cambiar tus herramientas actuales.',
    cta: 'Dinos qué software usas',
    whatsappText: 'Hola, quiero saber si Ancora puede conectarse con el software que uso actualmente en mi negocio.',
    heroImage: '/equipo-colaborando.jpg',
    heroImageAlt: 'Equipo conectando herramientas, datos e integraciones para automatizar procesos',
    who: ['Negocios con software propio', 'Centros con agenda', 'Equipos con CRM', 'Operativas en Sheets', 'Negocios con varias plataformas', 'Empresas que no quieren migrar'],
    outcomes: [
      'Mantener el software actual siempre que sea posible',
      'Conectar WhatsApp con agenda, CRM o base de datos',
      'Evitar copiar datos manualmente entre herramientas',
      'Crear automatizaciones a medida alrededor de la operativa',
      'Medir conversiones, campañas y oportunidades reales',
    ],
    modules: [
      { title: 'Mapeo de operativa', description: 'Analizamos canales, herramientas, datos y puntos donde se pierde tiempo o venta.', icon: DatabaseZap },
      { title: 'Conectores', description: 'APIs, webhooks, automatizaciones, Gmail, Sheets, calendarios y herramientas verticales.', icon: PlugZap },
      { title: 'Capa IA', description: 'Atención, clasificación, resúmenes, recomendaciones y generación de contenido.', icon: Bot },
      { title: 'Control y medición', description: 'Logs, paneles, métricas y alertas para saber qué funciona.', icon: LineChart },
    ],
    integrations: ['Flowww', 'TIMP', 'Booksy', 'Treatwell', 'Google Calendar', 'WhatsApp', 'Instagram', 'Google Business', 'Gmail', 'Sheets', 'Stripe', 'APIs'],
    proof: 'La ventaja no es tener “otro bot”, sino hacer que las herramientas que ya usas funcionen juntas.',
    faq: [
      { q: '¿Y si mi software no tiene integración oficial?', a: 'Lo revisamos. A veces se puede conectar por API, webhook, email, exportaciones o automatizaciones alrededor del proceso.' },
      { q: '¿Tengo que cambiar a otra agenda?', a: 'No es el objetivo. Primero intentamos conectar o rodear el sistema que ya funciona en tu negocio.' },
      { q: '¿Las integraciones entran en 9,99€?', a: 'No. El WhatsApp Base es la entrada; integraciones a medida se presupuestan aparte según complejidad.' },
    ],
  },
};

export const LANDING_LINKS = Object.values(USE_CASES).map(({ key, path, eyebrow, title, description }) => ({
  key,
  path,
  eyebrow,
  title,
  description,
}));

export const UseCaseLanding: React.FC<{ useCase: UseCase }> = ({ useCase }) => {
  const whatsappHref = `${WHATSAPP_LINK}?text=${encodeURIComponent(useCase.whatsappText)}`;

  useEffect(() => {
    const canonicalUrl = `https://www.ancoraautomations.es${useCase.path}`;
    const imageUrl = `https://www.ancoraautomations.es${useCase.heroImage}`;

    const ensureMeta = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, key);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    document.title = useCase.seoTitle;
    ensureMeta('meta[name="description"]', 'name', 'description', useCase.seoDescription);
    ensureMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    ensureMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    ensureMeta('meta[property="og:title"]', 'property', 'og:title', useCase.seoTitle);
    ensureMeta('meta[property="og:description"]', 'property', 'og:description', useCase.seoDescription);
    ensureMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    ensureMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', useCase.seoTitle);
    ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', useCase.seoDescription);
    ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
  }, [useCase]);

  return (
    <main className="bg-[#faf8ff] text-slate-900">
      <section className="relative overflow-hidden bg-slate-950 text-white pt-36 pb-24 dark-noise min-h-[640px] flex items-center">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/hero-poster.jpg"
            className="w-full h-full object-cover opacity-40"
            onLoadedData={(event) => {
              event.currentTarget.play().catch(() => undefined);
            }}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-950/45 to-slate-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-slate-950/60" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_70%_at_50%_30%,#000_60%,transparent_100%)] pointer-events-none" />
        </div>
        <div className="absolute top-24 right-10 w-72 h-72 bg-emerald-400/10 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full text-sm font-bold uppercase tracking-widest bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 mb-6">
                <CheckCircle2 size={16} />
                {useCase.eyebrow}
              </span>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight mb-6">
                {useCase.title}
              </h1>
              <p className="text-xl text-emerald-200 font-semibold mb-4">{useCase.highlight}</p>
              <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-8">{useCase.description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 font-extrabold text-slate-950 shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5 transition-transform"
                >
                  <MessageCircle size={20} />
                  {useCase.cta}
                </a>
                <a
                  href="#como-funciona"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  Ver módulos
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl shadow-2xl">
              <div className="relative overflow-hidden rounded-2xl mb-5 min-h-[220px] md:min-h-[280px]">
                <img
                  src={useCase.heroImage}
                  alt={useCase.heroImageAlt}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 border border-emerald-300/30 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-100 mb-3">
                    <Star size={14} />
                    Aplicado a tu operativa
                  </span>
                  <p className="text-sm text-slate-100 leading-relaxed">{useCase.proof}</p>
                </div>
              </div>
              <h2 className="text-xl font-black mb-5">Para negocios como:</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {useCase.who.map((item) => (
                  <div key={item} className="rounded-2xl bg-white/7 border border-white/10 px-4 py-3 text-sm text-slate-200">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Resultado práctico</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Qué automatizamos en este caso</h2>
            <p className="text-slate-500 text-lg">No vendemos una herramienta genérica: diseñamos el flujo alrededor de cómo trabaja tu negocio.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {useCase.outcomes.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <CheckCircle2 className="text-emerald-600 mt-0.5 flex-shrink-0" size={22} />
                <span className="text-slate-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="como-funciona" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Módulos</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Empieza por lo esencial y amplía cuando tenga retorno</h2>
            <p className="text-slate-500 text-lg">WhatsApp Base es la entrada; integraciones, ventas, voz y marketing se añaden como módulos según el flujo real.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {useCase.modules.map((module) => {
              const Icon = module.icon;
              return (
                <div key={module.title} className="rounded-3xl bg-white border border-slate-100 p-6 shadow-sm hover:-translate-y-1 transition-transform">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
                    <Icon size={24} className="text-emerald-700" />
                  </div>
                  <h3 className="font-black text-lg mb-3">{module.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{module.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-start">
            <div>
              <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Integraciones</span>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Conectamos con tu stack actual</h2>
              <p className="text-slate-500 text-lg leading-relaxed mb-6">Si ya tienes una herramienta que funciona, no la tiramos. La usamos como base para que la IA atienda, clasifique, recomiende y ejecute mejor.</p>
              <Link to="/integraciones-software-ia" className="inline-flex items-center gap-2 font-bold text-emerald-700 hover:text-emerald-800">
                Ver landing de integraciones
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {useCase.integrations.map((integration) => (
                <span key={integration} className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                  {integration}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <span className="text-emerald-300 font-bold uppercase tracking-widest text-sm">Preguntas habituales</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3">Antes de montar nada</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {useCase.faq.map((item) => (
              <div key={item.q} className="rounded-3xl border border-white/10 bg-white/5 p-6">
                <h3 className="font-black mb-3">{item.q}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-cyan-400 p-8 md:p-10 text-slate-950 text-center">
            <FileSignature size={34} className="mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black mb-4">¿Quieres verlo aplicado a tu negocio?</h2>
            <p className="text-slate-900/80 max-w-2xl mx-auto mb-8">Dinos qué canales usas, qué software tienes y dónde se pierden más oportunidades. Te diremos qué automatizar primero y qué dejar para más adelante.</p>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-8 py-4 font-extrabold text-white shadow-xl hover:-translate-y-0.5 transition-transform"
            >
              <MessageCircle size={20} />
              {useCase.cta}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
